import React from 'react';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Shuffle, 
  Repeat, 
  Repeat1, 
  Volume2, 
  Volume1, 
  VolumeX 
} from 'lucide-react';
import { IconButton, Tooltip } from '@/components/ui';
import { usePlayer } from '../PlayerProvider';
import { useAppDispatch, useAppSelector } from '@/store/store';
import {
  toggleRepeatMode,
  toggleShuffleMode,
  selectRepeatMode,
  selectShuffleMode,
  selectQueue,
} from '../playerSlice';

interface PlayerControlsProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showVolumeControl?: boolean;
  showSkipControls?: boolean;
  showModeControls?: boolean;
  showPlayButton?: boolean;
}

const PlayerControls: React.FC<PlayerControlsProps> = ({
  className = '',
  size = 'medium',
  showVolumeControl = true,
  showSkipControls = true,
  showModeControls = true,
  showPlayButton = true,
}) => {
  const dispatch = useAppDispatch();
  const {
    isPlaying,
    currentVideo,
    volume,
    isMuted,
    playVideo,
    pauseVideo,
    skipNext,
    skipPrevious,
    setVolume,
    toggleMute,
  } = usePlayer();
  
  const repeatMode = useAppSelector(selectRepeatMode);
  const shuffleMode = useAppSelector(selectShuffleMode);
  const queue = useAppSelector(selectQueue);
  
  const sizeClasses = {
    small: {
      button: 'w-8 h-8 text-sm',
      mainButton: 'w-10 h-10 text-lg',
      icon: 'w-4 h-4',
    },
    medium: {
      button: 'w-10 h-10 text-base',
      mainButton: 'w-12 h-12 text-xl',
      icon: 'w-5 h-5',
    },
    large: {
      button: 'w-12 h-12 text-lg',
      mainButton: 'w-14 h-14 text-2xl',
      icon: 'w-6 h-6',
    },
  };
  
  const transportIconSize: Record<typeof size, 'sm' | 'md' | 'lg'> = {
    small: 'sm',
    medium: 'md',
    large: 'lg',
  };

  const mainIconSize: Record<typeof size, 'sm' | 'md' | 'lg'> = {
    small: 'md',
    medium: 'lg',
    large: 'lg',
  };

  const transportButtonClass = 'hover:scale-110 hover:shadow-lg disabled:hover:scale-100';

  const handlePlayPause = () => {
    if (!currentVideo) return;
    
    if (isPlaying) {
      pauseVideo();
    } else {
      const videoWithDates = {
        ...currentVideo,
        createdAt: new Date(currentVideo.createdAt),
        updatedAt: new Date(currentVideo.updatedAt),
        publishedAt: new Date(currentVideo.publishedAt),
        dateAdded: new Date(currentVideo.dateAdded),
      };
      playVideo(videoWithDates);
    }
  };
  
  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(event.target.value);
    setVolume(newVolume);
  };
  
  const getRepeatIcon = () => {
    const iconSize = sizeClasses[size].icon;
    switch (repeatMode) {
      case 'one':
        return <Repeat1 className={iconSize} />;
      case 'all':
        return <Repeat className={iconSize} />;
      default:
        return <Repeat className={iconSize} />;
    }
  };
  
  const getRepeatLabel = () => {
    switch (repeatMode) {
      case 'one':
        return 'Repeat: One track';
      case 'all':
        return 'Repeat: All tracks';
      default:
        return 'Repeat: Off';
    }
  };
  
  return (
    <div className={`flex items-center justify-center space-x-2 ${className}`}>
      {showModeControls && (
        <>
          <Tooltip label={shuffleMode ? 'Shuffle: On' : 'Shuffle: Off'}>
            <IconButton
              onClick={() => dispatch(toggleShuffleMode())}
              variant="ghost"
              size={transportIconSize[size]}
              disabled={queue.length <= 1}
              aria-label={shuffleMode ? 'Disable shuffle' : 'Enable shuffle'}
              className={`
                ${transportButtonClass}
                ${shuffleMode
                  ? 'bg-meradio-secondary text-white'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                }
              `}
            >
              <Shuffle className={sizeClasses[size].icon} />
            </IconButton>
          </Tooltip>

          {showSkipControls && (
            <Tooltip label="Previous track">
              <IconButton
                onClick={skipPrevious}
                variant="ghost"
                size={transportIconSize[size]}
                disabled={queue.length <= 1}
                aria-label="Previous track"
                className={`${transportButtonClass} bg-white bg-opacity-20 text-white hover:bg-opacity-30`}
              >
                <SkipBack className={sizeClasses[size].icon} />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}

      {showPlayButton && (
        <Tooltip label={isPlaying ? 'Pause' : 'Play'}>
          <IconButton
            onClick={handlePlayPause}
            disabled={!currentVideo}
            variant="solid"
            size={mainIconSize[size]}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className={`
              ${transportButtonClass} font-bold shadow-lg
              ${size === 'large' ? 'w-14 h-14' : ''}
            `}
          >
            {isPlaying ? (
              <Pause className={sizeClasses[size].icon} fill="currentColor" />
            ) : (
              <Play className={sizeClasses[size].icon} fill="currentColor" />
            )}
          </IconButton>
        </Tooltip>
      )}

      {showSkipControls && (
        <Tooltip label="Next track">
          <IconButton
            onClick={skipNext}
            variant="ghost"
            size={transportIconSize[size]}
            disabled={queue.length <= 1}
            aria-label="Next track"
            className={`${transportButtonClass} bg-white bg-opacity-20 text-white hover:bg-opacity-30`}
          >
            <SkipForward className={sizeClasses[size].icon} />
          </IconButton>
        </Tooltip>
      )}

      {showModeControls && (
        <Tooltip label={getRepeatLabel()}>
          <IconButton
            onClick={() => dispatch(toggleRepeatMode())}
            variant="ghost"
            size={transportIconSize[size]}
            aria-label={`Toggle repeat mode. Current: ${repeatMode}`}
            className={`
              ${transportButtonClass}
              ${repeatMode !== 'off'
                ? 'bg-meradio-secondary text-white'
                : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }
            `}
          >
            {getRepeatIcon()}
          </IconButton>
        </Tooltip>
      )}

      {showVolumeControl && (
        <div className="flex items-center space-x-2 ml-4">
          <Tooltip label={isMuted ? 'Unmute' : 'Mute'}>
            <IconButton
              onClick={toggleMute}
              variant="ghost"
              size={transportIconSize[size]}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              className={`${transportButtonClass} bg-white bg-opacity-20 text-white hover:bg-opacity-30`}
            >
              {isMuted ? (
                <VolumeX className={sizeClasses[size].icon} />
              ) : volume > 50 ? (
                <Volume2 className={sizeClasses[size].icon} />
              ) : (
                <Volume1 className={sizeClasses[size].icon} />
              )}
            </IconButton>
          </Tooltip>

          <div className="hidden md:flex items-center space-x-2">
            <Tooltip label={`Volume: ${isMuted ? 0 : volume}%`}>
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="
                  w-20 h-1 bg-white bg-opacity-20 rounded-lg appearance-none cursor-pointer
                  slider-thumb:appearance-none slider-thumb:w-3 slider-thumb:h-3
                  slider-thumb:rounded-full slider-thumb:bg-white slider-thumb:cursor-pointer
                  hover:bg-opacity-30 transition-all duration-200
                "
                style={{
                  background: `linear-gradient(to right, white 0%, white ${isMuted ? 0 : volume}%, rgba(255,255,255,0.2) ${isMuted ? 0 : volume}%, rgba(255,255,255,0.2) 100%)`,
                }}
                aria-label="Volume control"
              />
            </Tooltip>
            <div className="text-xs text-white font-mono w-8 text-right">
              {isMuted ? 0 : volume}
            </div>
          </div>
        </div>
      )}

      <style>{`
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 2px;
          outline: none;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          transition: all 0.2s ease;
        }
        
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

export default PlayerControls;
